import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { TranslationsSmartCommandsApiService } from '@shared/modules/translations/data-access/translations-data-access/translations-smart-commands-api.service';
import { SelectControlSingleOption } from '@shared/modules/form-controls/select-control/select-control.component';
import { AdditionalTranslateFromLanguage } from '@shared/modules/smart-commands/common/translations/interfaces/responses';
import { EnumObjectValue } from '@shared/utils/common/types';
import { ModalFormService } from '@shared/forms/shell/modal-form/modal-form.service';
import { LanguageSelectorFormComponent } from '@shared/modules/translations/components/language-selector-form/language-selector-form.component';
import { LanguageSelectorFormModule } from '@shared/modules/translations/components/language-selector-form/language-selector-form.module';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';

@Component({
  selector: 'loop-change-translations-language',
  templateUrl: './change-translations-language.component.html',
  styleUrls: ['./change-translations-language.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChangeTranslationsLanguageComponent implements OnDestroy {
  private readonly translationsSmartCommandsApiService: TranslationsSmartCommandsApiService = inject(
    TranslationsSmartCommandsApiService
  );
  private readonly modalFormService: ModalFormService = inject(ModalFormService);

  protected readonly toLanguage$: ReplaySubject<string> = new ReplaySubject(1);
  @Input() set toLanguage(toLanguage: string) {
    this.toLanguage$.next(toLanguage);
  }

  private readonly fromLanguage$: ReplaySubject<string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>> =
    new ReplaySubject(1);
  @Input() set fromLanguage(fromLanguage: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>) {
    this.fromLanguage$.next(fromLanguage);
  }
  @Input() isOriginal?: boolean;

  @Output() translationsOptionsChange: EventEmitter<{
    from?: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>;
    to?: string;
    isOriginal?: boolean;
  }> = new EventEmitter();

  private readonly languageOptions$: Observable<SelectControlSingleOption[]> = this.translationsSmartCommandsApiService
    .getAvailableLanguages$()
    .pipe(
      map(languages => {
        return languages.languages.map(language => {
          return {
            label: language.name,
            value: language.language
          } satisfies SelectControlSingleOption;
        });
      }),
      publishReplay(1),
      refCount()
    );

  protected readonly languageTranslateOptions$: Observable<{ from: string; to: string }> = combineLatest([
    this.fromLanguage$,
    this.toLanguage$,
    this.languageOptions$
  ]).pipe(
    map(([fromLanguage, toLanguage, languageOptions]) => {
      return {
        from:
          fromLanguage === AdditionalTranslateFromLanguage.detect
            ? 'Detect language'
            : languageOptions.find(l => l.value === fromLanguage).label,
        to: languageOptions.find(l => l.value === toLanguage).label
      };
    })
  );

  protected openLanguageSelectorForm(): void {
    this.modalFormService.create({
      modalTitle$: of('Change language'),
      formComponent: {
        component: LanguageSelectorFormComponent,
        module: LanguageSelectorFormModule
      },
      defaults$: combineLatest([this.fromLanguage$, this.toLanguage$]).pipe(
        map(([from, to]) => {
          return {
            from: from,
            to: to
          };
        })
      ),
      afterSubmit$: (value: { from: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>; to: string }) => {
        return of(value);
      },
      observer: (value: { from: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>; to: string }) => {
        this.translationsOptionsChange.next({
          from: value.from,
          to: value.to,
          isOriginal: false
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.toLanguage$.complete();
    this.fromLanguage$.complete();
  }
}
